import React from 'react';
import Redux from 'redux';
import { connect } from 'react-redux';

import { clearDataAction } from '#actionCreators';

import {
    createConnectedRequestCoordinator,
    createRequestClient,
    methods,
    NewProps,
    ClientAttributes,
} from '#request';

import AuthContext from '#components/AuthContext';

import * as types from '#constants/types';

import Multiplexer from './Multiplexer';

interface State {
    userDetail: types.UserDetail | undefined;
    isAuthenticated: boolean;
}
interface OwnProps {
}
interface PropsFromDispatch {
    clearData: typeof clearDataAction;
}
type ReduxProps = OwnProps & PropsFromDispatch;

const mapDispatchToProps = (dispatch: Redux.Dispatch): PropsFromDispatch => ({
    clearData: () => dispatch(clearDataAction()),
});

interface Params {
    setUserDetail: (isAuthenticated: boolean, userDetail?: types.UserDetail) => void;
}

type Props = NewProps<ReduxProps, Params>;

const requests: {[key: string]: ClientAttributes<ReduxProps, Params>} = {
    userDetailRequest: {
        url: '/me/',
        method: methods.GET,
        onMount: true,
        onSuccess: ({ params, response }) => {
            const userDetail = response as types.UserDetail;

            if (params) {
                params.setUserDetail(true, userDetail);
            }
        },
        onFailure: ({ status, params }) => {
            if ((status === 401 || status === 403) && params) {
                params.setUserDetail(false);
            }
        },
    },
    logoutRequest: {
        url: '/logout/',
        method: methods.POST,
        onMount: false,
        onSuccess: ({ params }) => {
            if (params) {
                params.setUserDetail(false);
            }
        },
    },
};

/* Loads required info from server */
// eslint-disable-next-line react/prefer-stateless-function
class App extends React.Component<Props, State> {
    public constructor(props: Props) {
        super(props);

        this.state = {
            userDetail: undefined,
            isAuthenticated: false,
        };

        props.setDefaultRequestParams({
            setUserDetail: this.setUserDetail,
        });
    }

    private setUserDetail = (
        isAuthenticated: boolean,
        userDetail?: types.UserDetail | undefined,
    ) => {
        // NOTE: clearing out atom/domain
        if (!isAuthenticated) {
            this.props.clearData();
        }

        this.setState({
            isAuthenticated,
            userDetail,
        });
    }

    private logout = () => {
        const {
            requests: {
                logoutRequest,
            },
        } = this.props;

        logoutRequest.do({
            setUserDetail: this.setUserDetail,
        });
    }

    public render() {
        const {
            requests: {
                userDetailRequest: {
                    pending: userDetailRequestPending,
                },
                logoutRequest: {
                    pending: logoutRequestPending,
                },
            },
        } = this.props;

        const {
            isAuthenticated,
            userDetail,
        } = this.state;

        const authContextValue = {
            isAuthenticated,
            setUserDetail: this.setUserDetail,
            logout: this.logout,
        };

        return (
            <AuthContext.Provider value={authContextValue}>
                <Multiplexer
                    pending={userDetailRequestPending || logoutRequestPending}
                    logoutPending={logoutRequestPending}
                    isAuthenticated={isAuthenticated}
                    userDetail={userDetail}
                />
            </AuthContext.Provider>
        );
    }
}

export default connect(undefined, mapDispatchToProps)(
    createConnectedRequestCoordinator<ReduxProps>()(
        createRequestClient(requests)(App),
    ),
);
